import React, { useState } from 'react';
import { useActions, useCards, useBulkUpdateActionsMutation, useBulkUpdateCardsMutation, getUserValue } from '../../utils/reactQueryHooks';
import useQuickActionPresets from '../../hooks/useQuickActionPresets';
import ThresholdUpdateModal from '../common/ThresholdUpdateModal';
import QuickActionPresetModal from './QuickActionPresetModal';
import {
  DEFAULT_LIGHT_EXTEND_OPTIONS,
  DEFAULT_RUN_NOW_OPTIONS,
  QUICK_ACTION_RECIPES,
  QUICK_ACTION_TYPE_FILTERS,
  buildPresetSnapshot,
  describePresetOptions,
  executeQuickActions,
  getExecutionRecipe,
  getPresetRecipeDefinition,
  isExtendLightsPreset,
  selectActionsByPreset,
  selectActionsByTypes,
} from '../../utils/quickActionUtils';
import '../../styles/actions.css';

const TYPE_SNAPSHOT_PREFIX = 'canopy.quickActionTypeSnapshots';

function snapshotStorageKey() {
  return `${TYPE_SNAPSHOT_PREFIX}.${getUserValue() || 'anonymous'}`;
}

function readTypeSnapshots() {
  try {
    const raw = localStorage.getItem(snapshotStorageKey());
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.warn('Unable to read quick action type snapshots:', error);
    return {};
  }
}

function writeTypeSnapshots(snapshots) {
  localStorage.setItem(snapshotStorageKey(), JSON.stringify(snapshots));
}

function getFilterIcon(filterId) {
  if (filterId === 'light') return 'ri-lightbulb-flash-line';
  if (filterId === 'irrigation') return 'ri-drop-line';
  if (filterId === 'humidity') return 'ri-water-percent-line';
  return 'ri-windy-line';
}

function presetRecipeLabel(presetRecipe) {
  return getPresetRecipeDefinition(presetRecipe).label;
}

export default function QuickActionsPage() {
  const { data: actions = [], isLoading: actionsLoading } = useActions();
  const { data: cards = [], isLoading: cardsLoading } = useCards();
  const bulkUpdateActionsMutation = useBulkUpdateActionsMutation();
  const bulkUpdateCardsMutation = useBulkUpdateCardsMutation();
  const {
    presets,
    savePreset,
    deletePreset,
    updatePresetSnapshot,
  } = useQuickActionPresets();

  const [editingPreset, setEditingPreset] = useState(null);
  const [showPresetForm, setShowPresetForm] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [typeSnapshots, setTypeSnapshots] = useState(readTypeSnapshots);

  const isLoading = actionsLoading || cardsLoading;

  const persistTypeSnapshots = (nextSnapshots) => {
    setTypeSnapshots(nextSnapshots);
    writeTypeSnapshots(nextSnapshots);
  };

  const runTargets = async ({ id, label, recipe, targetActions, options, snapshots, onSnapshot }) => {
    setRunningId(id);
    try {
      if (onSnapshot) onSnapshot();
      const actionUpdates = [];
      const cardUpdates = [];
      const result = await executeQuickActions({
        recipe,
        label,
        targetActions,
        cards,
        options,
        snapshots,
        onActionSuccess: (updatedAction) => {
          actionUpdates.push(updatedAction);
        },
        onCardSuccess: (updatedCard) => {
          cardUpdates.push(updatedCard);
        },
      });

      const dedupedCardUpdates = [...new Map(
        cardUpdates.map((card) => [card.macAddress, card])
      ).values()];
      const dedupedActionUpdates = [...new Map(
        actionUpdates.map((action) => [action.macAddress, action])
      ).values()];

      // Persist every touched card in a single optimistic write + POST, rather
      // than one round-trip per device. A failure rolls the whole batch back.
      if (dedupedCardUpdates.length > 0) {
        try {
          await bulkUpdateCardsMutation.mutateAsync(dedupedCardUpdates);
        } catch (error) {
          for (const updatedCard of dedupedCardUpdates) {
            result.failedDevices.push({
              macAddress: updatedCard.macAddress,
              actuatorType: 'light',
              name: updatedCard.sensorDescription || updatedCard.macAddress,
              reason: `Device updated, but app sync failed: ${error.message}`,
            });
          }
          result.success = false;
        }
      }

      if (dedupedActionUpdates.length > 0) {
        try {
          await bulkUpdateActionsMutation.mutateAsync(dedupedActionUpdates);
        } catch (error) {
          for (const updatedAction of dedupedActionUpdates) {
            result.failedDevices.push({
              ...updatedAction,
              name: updatedAction.actuatorDescription || updatedAction.macAddress,
              reason: `Device updated, but app sync failed: ${error.message}`,
            });
          }
          result.success = false;
        }
      }

      setUpdateResult(result);
      setShowResultModal(true);
    } finally {
      setRunningId(null);
    }
  };

  const runTypeFilter = async (filter) => {
    const targetActions = selectActionsByTypes(actions, filter.actuatorTypes);
    if (targetActions.length === 0) return;

    await runTargets({
      id: `type-${filter.id}`,
      label: filter.recipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS ? `Extend ${filter.label}` : `Run ${filter.label}`,
      recipe: filter.recipe,
      targetActions,
      options: filter.recipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS
        ? DEFAULT_LIGHT_EXTEND_OPTIONS
        : DEFAULT_RUN_NOW_OPTIONS,
      onSnapshot:
        filter.recipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS
          ? () => {
              const nextSnapshots = {
                ...typeSnapshots,
                [filter.id]: buildPresetSnapshot({ actionMacs: targetActions.map((action) => action.macAddress) }, actions, cards),
              };
              persistTypeSnapshots(nextSnapshots);
            }
          : undefined,
    });
  };

  const restoreTypeFilter = async (filter) => {
    const targetActions = selectActionsByTypes(actions, filter.actuatorTypes);
    await runTargets({
      id: `restore-${filter.id}`,
      label: `Restore ${filter.label}`,
      recipe: QUICK_ACTION_RECIPES.RESTORE_LIGHTS,
      targetActions,
      snapshots: typeSnapshots[filter.id] || {},
    });
  };

  const runPreset = async (preset) => {
    const targetActions = selectActionsByPreset(preset, actions);
    if (targetActions.length === 0) return;

    await runTargets({
      id: `preset-${preset.id}`,
      label: preset.name,
      recipe: getExecutionRecipe(preset.recipe),
      targetActions,
      options: preset.options,
      onSnapshot:
        isExtendLightsPreset(preset.recipe)
          ? () => updatePresetSnapshot(preset.id, buildPresetSnapshot(preset, actions, cards))
          : undefined,
    });
  };

  const restorePreset = async (preset) => {
    const targetActions = selectActionsByPreset(preset, actions);
    await runTargets({
      id: `preset-restore-${preset.id}`,
      label: `Restore ${preset.name}`,
      recipe: QUICK_ACTION_RECIPES.RESTORE_LIGHTS,
      targetActions,
      snapshots: preset.lastSnapshot || {},
    });
  };

  const handleSavePreset = (preset) => {
    savePreset(preset);
    setEditingPreset(null);
  };

  const closePresetModal = () => {
    setEditingPreset(null);
    setShowPresetForm(false);
  };

  const openNewPresetModal = () => {
    setEditingPreset(null);
    setShowPresetForm(true);
  };

  return (
    <main className="quick-actions-page">
      <div className="quick-actions-page-toolbar">
        <button
          type="button"
          className="green-button quick-action-new-preset-btn"
          onClick={openNewPresetModal}
        >
          <i className="ri-add-line"></i>
          New preset
        </button>
      </div>

      {isLoading && <p className="quick-action-status">Loading action devices...</p>}

      {!isLoading && (
        <>
          <section className="quick-action-section">
            <div className="actions-header">
              <h3 className="section-title">Type filters</h3>
            </div>
            <p className="quick-action-section-desc">Fast account-wide controls by action type.</p>
            <div className="quick-action-filter-list">
              {QUICK_ACTION_TYPE_FILTERS.map((filter) => {
                const targetActions = selectActionsByTypes(actions, filter.actuatorTypes);
                const isLightFilter = filter.recipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS;
                const canRestore = isLightFilter && Object.keys(typeSnapshots[filter.id] || {}).length > 0;

                return (
                  <article key={filter.id} className="quick-action-card">
                    <div className={`quick-action-card-icon quick-action-card-icon--${filter.id}`}>
                      <i className={getFilterIcon(filter.id)}></i>
                    </div>
                    <div className="quick-action-card-body">
                      <h3>{filter.label}</h3>
                      <p>
                        {targetActions.length} configured device{targetActions.length === 1 ? '' : 's'}.
                      </p>
                      <small>
                        {isLightFilter ? 'Extends schedule until 11:00 PM by default.' : 'Uses the immediate trigger path.'}
                      </small>
                    </div>
                    <div className="quick-action-card-actions">
                      <button
                        type="button"
                        className="quick-action-primary-btn"
                        disabled={targetActions.length === 0 || runningId !== null}
                        onClick={() => runTypeFilter(filter)}
                      >
                        {runningId === `type-${filter.id}` ? 'Running...' : isLightFilter ? 'Extend' : 'Run now'}
                      </button>
                      {isLightFilter && (
                        <button
                          type="button"
                          className="quick-action-secondary-btn"
                          disabled={!canRestore || runningId !== null}
                          onClick={() => restoreTypeFilter(filter)}
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="quick-action-section">
            <div className="actions-header">
              <h3 className="section-title">Saved presets</h3>
            </div>
            <p className="quick-action-section-desc">Named groups for shelves, zones, or recurring work sessions.</p>

            {presets.length === 0 && (
              <div className="quick-action-empty-panel">
                <p>No presets yet. Create one for a recurring group like “Grow shelf lights”.</p>
              </div>
            )}

            {presets.length > 0 && (
              <div className="quick-action-preset-list">
                {presets.map((preset) => {
                  const targetActions = selectActionsByPreset(preset, actions);
                  const presetOptionSummary = describePresetOptions(preset, actions);
                  const canRestore =
                    isExtendLightsPreset(preset.recipe) &&
                    Object.keys(preset.lastSnapshot || {}).length > 0;

                  return (
                    <article key={preset.id} className="quick-action-preset">
                      <div>
                        <h3>{preset.name}</h3>
                        <p>{presetRecipeLabel(preset.recipe)} · {targetActions.length} device{targetActions.length === 1 ? '' : 's'}</p>
                        {presetOptionSummary && (
                          <small>{presetOptionSummary}</small>
                        )}
                      </div>
                      <div className="quick-action-preset-actions">
                        <button
                          type="button"
                          className="quick-action-primary-btn"
                          disabled={targetActions.length === 0 || runningId !== null}
                          onClick={() => runPreset(preset)}
                        >
                          {runningId === `preset-${preset.id}` ? 'Running...' : 'Run'}
                        </button>
                        {isExtendLightsPreset(preset.recipe) && (
                          <button
                            type="button"
                            className="quick-action-secondary-btn"
                            disabled={!canRestore || runningId !== null}
                            onClick={() => restorePreset(preset)}
                          >
                            Restore
                          </button>
                        )}
                        <button
                          type="button"
                          className="quick-action-link-btn"
                          onClick={() => {
                            setEditingPreset(preset);
                            setShowPresetForm(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="quick-action-link-btn quick-action-link-btn--danger"
                          onClick={() => deletePreset(preset.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <QuickActionPresetModal
        isOpen={showPresetForm}
        onClose={closePresetModal}
        actions={actions}
        cards={cards}
        preset={editingPreset}
        onSave={handleSavePreset}
      />

      <ThresholdUpdateModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        updateResult={updateResult}
      />
    </main>
  );
}
